<template>
  <div v-if="hasValues" class="filter-panel">
    <div
      class="filter-header"
      @click="isOpen = !isOpen"
    >
      <div class="subtitle-1">{{ title }}</div>
      <v-icon>{{ isOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
    </div>
    <div v-show="isOpen" class="filter-content">
      <v-list dense class="pa-0">
        <v-list-item
          v-for="(stat, index) in stats"
          :key="index"
          class="px-3 py-1"
          :class="{ 'selected': isSelected(stat.rawValue) }"
          @click="toggleFilter(stat.rawValue)"
        >
          <v-list-item-content class="py-0">
            <v-list-item-title class="body-2">
              {{ getDisplayName(stat.rawValue) }}
            </v-list-item-title>
          </v-list-item-content>
          <v-list-item-action class="my-0">
            <div class="d-flex align-center">
              <span class="caption grey--text mr-2">{{ stat.count }}</span>
              <span class="caption grey--text">{{ stat.percentage }}%</span>
            </div>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex';

export default {
  name: 'FilterStats',
  props: {
    data: {
      type: Array,
      required: true
    },
    keyProp: {
      type: String,
      required: true
    },
    title: {
      type: String,
      default: 'Statistics'
    },
    mapping: {
      type: Object,
      default: () => ({})
    },
    filterKey: {
      type: String,
      required: true
    },
    alternativeValue: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      isOpen: false,
      originalFilter: {}
    }
  },
  created() {
    // Store the original filter state
    this.originalFilter = JSON.parse(JSON.stringify(this.filter));
  },
  computed: {
    ...mapState('filter', ['filter']),
    ...mapGetters('filter', [
      'allFilteredEvents'
    ]),
    hasValues() {
      return this.stats.length > 0;
    },
    stats() {
      // Only use the currently filtered events
      const filteredData = this.allFilteredEvents;
      const total = filteredData.length;
      const counts = {};

      // Count occurrences of each value in the filtered data
      filteredData.forEach(item => {
        const value = this.getNestedValue(item, this.keyProp);
        if (value !== null && value !== undefined) {
          counts[value] = (counts[value] || 0) + 1;
        }
      });

      // Convert to array and calculate percentages
      return Object.entries(counts)
        .map(([rawValue, count]) => ({
          rawValue,
          count,
          percentage: ((count / total) * 100).toFixed(1)
        }))
        .sort((a, b) => b.count - a.count);
    }
  },
  methods: {
    ...mapMutations({
      updateFilter: 'filter/SET_CURRENT_FILTER'
    }),
    ...mapActions({
      openEvent: 'filter/openEvent',
      info: 'noti/info'
    }),
    getDisplayName(value) {
      return this.mapping[value] || value;
    },
    getNestedValue(obj, path) {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : null;
      }, obj);
    },
    isSelected(value) {
      const currentFilter = this.filter[this.filterKey];
      if (!Array.isArray(currentFilter)) return false;

      if (this.alternativeValue) {
        // Find an event with matching display value and check if its alternative value is in the filter
        const matchingEvent = this.data.find(item =>
          this.getNestedValue(item, this.keyProp) === value
        );
        if (!matchingEvent) return false;
        const altValue = this.getNestedValue(matchingEvent, this.alternativeValue);
        const processedValue = this.processFilterValue(altValue);
        return currentFilter.includes(processedValue);
      }

      const processedValue = this.processFilterValue(value);
      return currentFilter.includes(processedValue);
    },
    async toggleFilter(value) {
      const currentFilter = this.filter[this.filterKey] || [];
      let newFilter;

      if (this.alternativeValue) {
        // Find an event with matching display value and get its alternative value
        const matchingEvent = this.data.find(item =>
          this.getNestedValue(item, this.keyProp) === value
        );
        if (!matchingEvent) return;

        const altValue = this.getNestedValue(matchingEvent, this.alternativeValue);
        const processedValue = this.processFilterValue(altValue);

        if (currentFilter.includes(processedValue)) {
          // If removing the last item, revert to original filter state
          if (currentFilter.length === 1) {
            newFilter = this.originalFilter[this.filterKey] || ['all'];
          } else {
            newFilter = currentFilter.filter(v => v !== processedValue);
          }
        } else {
          // If adding an item, remove "all" if it exists
          newFilter = currentFilter.filter(v => v !== 'all');
          newFilter.push(processedValue);
        }
      } else {
        const processedValue = this.processFilterValue(value);

        if (currentFilter.includes(processedValue)) {
          // If removing the last item, revert to original filter state
          if (currentFilter.length === 1) {
            newFilter = this.originalFilter[this.filterKey] || ['all'];
          } else {
            newFilter = currentFilter.filter(v => v !== processedValue);
          }
        } else {
          // If adding an item, remove "all" if it exists
          newFilter = currentFilter.filter(v => v !== 'all');
          newFilter.push(processedValue);
        }
      }

      // Create a new filter object with all existing values
      const updatedFilter = {
        ...this.filter,
        [this.filterKey]: newFilter
      };

      // Ensure all array properties are properly initialized
      const arrayProperties = [
        'hitters', 'runners', 'upClubs', 'stadions', 'spClubs', 'outfielders',
        'referees', 'game_ids', 'vedottu_vaara', 'vvo', 'was_wrong', 'wrong_type',
        'bases_state', 'marked', 'hit_outcome', 'outs', 'hit_number', 'hit_type',
        'outfield_formation', 'lapilyonti', 'tall_pitch', 'hitter_side', 'kotari',
        'superi', 'vaara_alla', 'play_outcome', 'mark_type', 'folderIds'
      ];

      arrayProperties.forEach(prop => {
        if (!Array.isArray(updatedFilter[prop])) {
          updatedFilter[prop] = this.originalFilter[prop] || ['all'];
        }
      });

      this.updateFilter(updatedFilter);
      if(!this.$vuetify.breakpoint.mobile) this.info('Suodatin päivitetty');

      this.$nextTick(() => {
        // Open the first event if available
        if (this.allFilteredEvents && this.allFilteredEvents.length > 0) {
          this.openEvent(this.allFilteredEvents[0]);
        }
      });
    },
    processFilterValue(value) {
      // Convert booleans to Finnish strings
      if (value === true || value === 'true') {
        return 'kylla';
      }
      if (value === false || value === 'false') {
        return 'ei';
      }

      // Convert stringified numbers to actual numbers
      if (typeof value === 'string' && !isNaN(value) && value.trim() !== '') {
        return Number(value);
      }

      return value;
    }
  }
}
</script>

<style lang="scss" scoped>
.filter-panel {
  background: #f5f5f5;
  border-radius: 4px;
  margin: 0.5rem 0;
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  min-height: 40px;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
}

.filter-content {
  background: white;
  border-radius: 0 0 4px 4px;
}

.v-list-item {
  min-height: 32px;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }

  &.selected {
    background: rgba(33, 150, 243, 0.1);
    font-weight: 500;

    .v-list-item__title {
      color: rgb(33, 150, 243);
    }
  }
}
</style>